import type { N43Account, N43Movement } from "./norma43";

const moneyPattern = "[+-]?(?:\\d{1,3}(?:\\.\\d{3})+|\\d+),\\d{2}";
const rowPattern = new RegExp(`^(\\d{2}/\\d{2}/\\d{4})\\s+(\\d{2}/\\d{2}/\\d{4})\\s+(\\d{3})\\s+(.+?)\\s+(${moneyPattern})\\s+EUR\\s+(${moneyPattern})\\s+EUR(?: (.*))?$`);
const cents = (value: string) => Math.round(Number(value.replace(/\./g, "").replace(",", ".")) * 100);
const isoDate = (value: string) => {
  const [day, month, year] = value.split("/");
  const iso = `${year}-${month}-${day}`;
  const date = new Date(`${iso}T00:00:00Z`);
  if (!Number.isFinite(date.getTime()) || date.toISOString().slice(0, 10) !== iso) {
    throw new Error(`Fecha no válida en el TXT: ${value}`);
  }
  return iso;
};

export const isSantanderTxt = (text: string) => /^Fecha Operación\s+Fecha Valor\s+Código\s+Concepto\s+Importe\s+Saldo\s+Número de documento\s+Referencia 1\s+Referencia 2\s+Información adicional\s*$/m.test(text.replace(/\r/g, ""));

/** Santander Consulta de movimientos: conserva el concepto y las cuatro columnas complementarias. */
export function parseSantanderTxt(text: string): N43Account[] {
  if (!isSantanderTxt(text)) return [];
  const lines = text.replace(/^\uFEFF/, "").replace(/\r/g, "").split("\n");
  const header = lines.findIndex((line) => line.startsWith("Fecha Operación"));
  const rows: { movement: N43Movement; balance: number; amount: number }[] = [];
  for (let i = header + 1; i < lines.length; i++) {
    const line = lines[i];
    if (!line.trim()) continue;
    const match = rowPattern.exec(line);
    if (!match) throw new Error(`No se puede leer el movimiento de la línea ${i + 1}. No se ha cargado ningún movimiento.`);
    const [, operation, value, code, concept, amountText, balanceText, tail = ""] = match;
    const amount = cents(amountText);
    const extra = tail.slice(58).trim();
    const concepts = [concept.trim(), extra].filter(Boolean);
    rows.push({
      amount, balance: cents(balanceText),
      movement: {
        index: 0, operationDate: isoDate(operation), valueDate: isoDate(value),
        commonConcept: "", ownConcept: code, amount: amount / 100,
        documentNumber: tail.slice(0, 26).trim(), reference1: tail.slice(26, 42).trim(),
        reference2: tail.slice(42, 58).trim(), concepts, conceptText: concepts.join(" "),
      },
    });
  }
  if (!rows.length) throw new Error("El TXT de Santander no contiene movimientos.");
  // La descarga de Santander está de más reciente a más antiguo. Revertir también
  // el orden dentro del mismo día es necesario para comprobar el saldo de cada fila.
  let ascending = true, descending = true;
  for (let i = 1; i < rows.length; i++) {
    const previous = rows[i - 1].movement.operationDate;
    const current = rows[i].movement.operationDate;
    if (current < previous) ascending = false;
    if (current > previous) descending = false;
  }
  if (!ascending && !descending) throw new Error("Los movimientos del TXT no están ordenados por fecha.");
  const followsBalances = (ordered: typeof rows) => ordered.every((row, i) => i === 0 || row.balance === ordered[i - 1].balance + row.amount);
  const ordered = descending && (!ascending || !followsBalances(rows)) ? [...rows].reverse() : rows;
  const first = ordered[0], last = ordered[ordered.length - 1];
  if (!first || !last) return [];
  const initial = first.balance - first.amount;
  const debits = ordered.filter((row) => row.amount < 0);
  const credits = ordered.filter((row) => row.amount >= 0);
  const debitTotal = -debits.reduce((sum, row) => sum + row.amount, 0);
  const creditTotal = credits.reduce((sum, row) => sum + row.amount, 0);
  const totals = { debitCount: debits.length, debitTotal: debitTotal / 100, creditCount: credits.length, creditTotal: creditTotal / 100 };
  const metadata = lines.slice(0, header).join("\n");
  const iban = /^Cuenta\s+(ES[\d ]+)$/m.exec(metadata)?.[1].replace(/ /g, "") ?? "";
  const period = /Movimientos Fecha desde (\d{2}\/\d{2}\/\d{4}) Fecha Hasta (\d{2}\/\d{2}\/\d{4})/.exec(metadata);
  return [{
    sourceFormat: "santander-txt", bank: iban.slice(4, 8), office: iban.slice(8, 12), account: iban.slice(14),
    holder: /^Titular\s+(.+)$/m.exec(metadata)?.[1].trim() ?? "Santander",
    currency: "978", startDate: period ? isoDate(period[1]) : first.movement.operationDate,
    endDate: period ? isoDate(period[2]) : last.movement.operationDate,
    initialBalance: initial / 100,
    movements: ordered.map((row, i) => ({ ...row.movement, index: i + 1 })),
    declared: { ...totals, finalBalance: last.balance / 100 },
    computed: { ...totals, finalBalance: (initial - debitTotal + creditTotal) / 100 },
    balanced: followsBalances(ordered),
  }];
}